import React, { useEffect, useRef } from 'react'
import { CSSObject } from '@emotion/react'

import {
  AlignItems,
  Box,
  BoxProps,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  JustifyContent,
  Text,
  TextAlign,
  ZIndex,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { LandingPageContentSections, trackCTAClick, TrackingActions } from '../../tracking'
import { CWCLinkButton } from '../CWCLinkButton/CWCLinkButton'
import { useFloatingCTAVisibility } from './useFloatingCTAVisibility'

export type CWCFloatingCTAProps = {
  /** Sign-up destination the CTA links to — same shape as every other CwC section. */
  signupUrl: string
  onCTAClick: () => void
}

type FloatingBarInnerProps = BoxProps & { visible: boolean }

const FloatingBarInner = withCitrusTheme<FloatingBarInnerProps>(
  (props: FloatingBarInnerProps) => {
    const { visible: _visible, ...boxProps } = props

    return <Box {...boxProps} />
  },
  (theme, props) => [
    {
      opacity: props.visible ? 1 : 0,
      // Hidden immediately, not just faded out — the exit transition below takes up to 540ms, and
      // during that window the bar would otherwise still be fully hit-testable (tabIndex/
      // aria-hidden toggle immediately, but pointer-events doesn't unless tied to `visible` too),
      // so a tap mid-fade could hit the fading CTA instead of the newly revealed content beneath it.
      pointerEvents: props.visible ? 'auto' : 'none',
      background: 'rgba(18,21,25,0.84)',
      backdropFilter: 'blur(22px) saturate(170%)',
      WebkitBackdropFilter: 'blur(22px) saturate(170%)',
      transition: 'transform 540ms cubic-bezier(.2,.8,.2,1), opacity 360ms ease',
      borderTopWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'rgba(255,255,255,0.12)',
    } as CSSObject,
    theme.responsiveStyle(
      ['16px 16px 0px 0px', '16px 16px 0px 0px', '16px', '16px'],
      ['borderRadius'],
    ) as CSSObject,
    theme.responsiveStyle(
      ['0px', '0px', '1px', '1px'],
      ['borderRightWidth', 'borderBottomWidth', 'borderLeftWidth'],
    ) as CSSObject,
    theme.responsiveStyle(
      [
        props.visible ? 'translateY(0)' : 'translateY(130%)',
        props.visible ? 'translateY(0)' : 'translateY(130%)',
        props.visible ? 'translateY(0)' : 'translateY(160%)',
        props.visible ? 'translateY(0)' : 'translateY(160%)',
      ],
      ['transform'],
    ) as CSSObject,
  ],
)

export function CWCFloatingCTA({ signupUrl, onCTAClick }: CWCFloatingCTAProps) {
  const isVisible = useFloatingCTAVisibility()
  const barRef = useRef<HTMLDivElement>(null)

  // Changing tabIndex/aria-hidden alone doesn't move focus — if a keyboard user has focused the
  // CTA and then scrolls it out of the visible range, the browser leaves focus sitting on an
  // opacity-0/translated, now aria-hidden control, which is both an ARIA violation (focus must
  // never be inside an aria-hidden subtree) and disorienting for keyboard/screen-reader users.
  // Blur it explicitly (moving focus to the document body) whenever the bar hides while focused.
  useEffect(() => {
    if (!isVisible && barRef.current?.contains(document.activeElement)) {
      ;(document.activeElement as HTMLElement).blur()
    }
  }, [isVisible])

  const handleAndTrackCTAClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    onCTAClick()
    trackCTAClick(TrackingActions.CLICK_GET_STARTED, LandingPageContentSections.FLOATING)
  }

  return (
    <Box
      ref={barRef}
      data-testid="cwcFloatingCTA"
      position={DisplayPosition.Fixed}
      left="0px"
      right="0px"
      bottom={['0px', '0px', '32px', '32px']}
      display={Display.Flex}
      justifyContent={JustifyContent.Center}
      zIndex={ZIndex.Sticky}
      style={{ pointerEvents: 'none' }}
    >
      <FloatingBarInner
        visible={isVisible}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.Center}
        gap={2}
        width={['100%', '100%', '366px', '366px']}
        paddingV={4}
        paddingH={3}
        paddingBottom={2}
        boxShadow={[
          '0 -18px 44px -18px rgba(0,0,0,0.6)',
          '0 -18px 44px -18px rgba(0,0,0,0.6)',
          '0 26px 64px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.07)',
          '0 26px 64px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.07)',
        ]}
        // Hides the whole bar (CTA + reassurance text) from assistive tech while it's off-screen —
        // not just the CTA — so a screen-reader user never lands on either while the bar is hidden.
        aria-hidden={isVisible ? undefined : true}
      >
        <CWCLinkButton
          href={signupUrl}
          onClick={handleAndTrackCTAClick}
          tabIndex={isVisible ? 0 : -1}
        >
          Get started with 50% off
        </CWCLinkButton>
        <Text color={Color.White} textAlign={TextAlign.Center} size={2}>
          <span style={{ fontWeight: FontWeight.Bold }}>Skip or cancel</span> for free anytime
        </Text>
      </FloatingBarInner>
    </Box>
  )
}
